/**
 * 消費一覧の列（プロダクト仕様書「消費一覧」の「表示項目」）。企業の列のあとに、4つのまとまりの列を並べる。
 */

import type { ReactNode } from 'react';

import { planLabels } from '@/components/credit-contracts/display';
import type { CategoryColumns, CompanyRow, ConsumptionList } from '@/lib/api/credit-consumption';
import { addMonths, monthLabel } from '@/lib/credit-contracts/month';

import { breakdownColumns, credit, integer, NONE, percent, terabytes } from './display';
import type { SortValue } from './sorting';
import type { Group } from './useConsumptionList';

export interface ListColumn {
  key: string;
  group: Group;
  /** 消費の内訳の上の段の見出し（表示区分）。1段の見出しは null */
  category: string | null;
  /** 上の段の見出し（category）に添える説明。「i」のアイコンにマウスを乗せると出す */
  categoryHint?: string;
  name: string;
  unit?: string;
  numeric: boolean;
  sortValue: (row: CompanyRow) => SortValue;
  render: (row: CompanyRow) => ReactNode;
}

const dec = (value: string | null): SortValue => ({ kind: 'decimal', value });

/** 料金プラン。なければ「なし」 */
export const planText = (row: {
  contract: { pricing_plan: CompanyRow['contract']['pricing_plan'] };
}) => (row.contract.pricing_plan ? planLabels[row.contract.pricing_plan] : 'なし');

function contractColumns(isCurrent: boolean): ListColumn[] {
  const g: Group = 'contract';
  const cols: ListColumn[] = [
    {
      key: 'plan',
      group: g,
      category: null,
      name: '料金プラン',
      numeric: false,
      sortValue: (r) => ({ kind: 'text', value: planText(r) }),
      render: (r) => planText(r),
    },
    {
      key: 'contract_credit',
      group: g,
      category: null,
      name: '契約クレジット数',
      unit: '（クレジット/月）',
      numeric: true,
      sortValue: (r) => dec(r.contract.contract_credit),
      render: (r) => credit(r.contract.contract_credit),
    },
    {
      key: 'monthly_fee',
      group: g,
      category: null,
      name: '月額料金',
      unit: '（円）',
      numeric: true,
      sortValue: (r) => dec(r.contract.monthly_fee),
      render: (r) => integer(r.contract.monthly_fee),
    },
    {
      key: 'consumption',
      group: g,
      category: null,
      name: '消費クレジット数',
      unit: '（クレジット）',
      numeric: true,
      sortValue: (r) => dec(r.consumption?.total ?? null),
      render: (r) => credit(r.consumption?.total),
    },
    {
      key: 'ratio',
      group: g,
      category: null,
      name: '契約に対する割合',
      unit: '（%）',
      numeric: true,
      sortValue: (r) => dec(r.ratio),
      render: (r) => percent(r.ratio),
    },
  ];
  if (isCurrent) {
    cols.push(
      {
        key: 'forecast',
        group: g,
        category: null,
        name: '月末の着地見込み',
        unit: '（クレジット）',
        numeric: true,
        sortValue: (r) => dec(r.forecast),
        render: (r) => credit(r.forecast),
      },
      {
        key: 'forecast_ratio',
        group: g,
        category: null,
        name: '月末の着地見込みの割合',
        unit: '（%）',
        numeric: true,
        sortValue: (r) => dec(r.forecast_ratio),
        render: (r) => percent(r.forecast_ratio),
      },
    );
  }
  return cols;
}

/** 過去6か月の平均の求め方の説明 */
const HISTORY_AVERAGE_HINT = '月の途中から始まった最初の月は、平均に含めません。';

/** 過去6か月の平均。月ごとの列の左に置く */
function historyAverageColumns(): ListColumn[] {
  return [
    {
      key: 'history_average',
      group: 'history',
      category: '過去6か月の平均',
      categoryHint: HISTORY_AVERAGE_HINT,
      name: '消費クレジット数',
      unit: '（クレジット）',
      numeric: true,
      sortValue: (r) => dec(r.history_average),
      render: (r) => credit(r.history_average),
    },
    {
      key: 'history_average_ratio',
      group: 'history',
      category: '過去6か月の平均',
      categoryHint: HISTORY_AVERAGE_HINT,
      name: '契約に対する割合',
      unit: '（%）',
      numeric: true,
      sortValue: (r) => dec(r.history_average_ratio),
      render: (r) => percent(r.history_average_ratio),
    },
  ];
}

/**
 * 過去6か月の消費クレジット。指定した月の前の月を左端にし、新しい月から並べる。
 * 月ごとに、消費クレジット数と、指定した月の契約クレジット数に対する割合を並べる
 */
function historyColumns(months: string[]): ListColumn[] {
  return months.flatMap((month, i): ListColumn[] => [
    {
      key: `history:${i}`,
      group: 'history',
      category: monthLabel(month),
      name: '消費クレジット数',
      unit: '（クレジット）',
      numeric: true,
      sortValue: (r) => dec(r.history[i]?.consumption ?? null),
      render: (r) => {
        const cell = r.history[i];
        if (!cell?.consumption) return NONE;
        return credit(cell.consumption);
      },
    },
    {
      key: `history_ratio:${i}`,
      group: 'history',
      category: monthLabel(month),
      name: '契約に対する割合',
      unit: '（%）',
      numeric: true,
      sortValue: (r) => dec(r.history[i]?.ratio ?? null),
      render: (r) => percent(r.history[i]?.ratio),
    },
  ]);
}

function snapshotColumns(): ListColumn[] {
  const g: Group = 'snapshots';
  const col = (
    key: keyof CompanyRow['snapshots'],
    name: string,
    unit: string | undefined,
    format: (v: string | null) => string,
  ): ListColumn => ({
    key,
    group: g,
    category: null,
    name,
    unit,
    numeric: true,
    sortValue: (r) => dec(r.snapshots[key]),
    render: (r) => format(r.snapshots[key]),
  });
  return [
    col('store_sku', '店舗×SKU', undefined, integer),
    col('s3_tb', 'S3容量', '（TB）', terabytes),
    col('user_count', 'ユーザー数', '（人）', integer),
    // 店舗向けと本部向けを分け、契約していない側は「-」にして、どちらを契約しているかを示す
    col('store_agent_store_sellers', 'ストアエージェント(店舗向け)の店舗数', undefined, integer),
    col('store_agent_hub_sellers', 'ストアエージェント(本部向け)の店舗数', undefined, integer),
    {
      key: 'data_connect',
      group: g,
      category: null,
      name: 'データコネクト',
      unit: '（クレジット/月）',
      numeric: true,
      sortValue: (r) => dec(r.data_connect),
      render: (r) => credit(r.data_connect),
    },
  ];
}

/** 消費の内訳。消費項目ごとに1列、表示区分ごとにまとめる */
export function breakdownListColumns(categories: CategoryColumns[]): ListColumn[] {
  return breakdownColumns(categories).map((c) => ({
    key: c.key,
    group: 'breakdown',
    category: c.group,
    name: c.label,
    unit: '（クレジット）',
    numeric: true,
    sortValue: (r) =>
      dec(r.consumption ? c.value(r.consumption.items, r.consumption.category_totals) : null),
    render: (r) =>
      credit(r.consumption ? c.value(r.consumption.items, r.consumption.category_totals) : null),
  }));
}

/** 企業の列のあとに並べる列 */
export function listColumns(data: ConsumptionList): ListColumn[] {
  const months = Array.from({ length: 6 }, (_, i) => addMonths(data.month, -(i + 1)));
  return [
    ...contractColumns(data.month === data.current_month),
    ...historyAverageColumns(),
    ...historyColumns(months),
    ...snapshotColumns(),
    ...breakdownListColumns(data.categories),
  ];
}

/** 企業の列の並べ替えの値 */
export function companySortValue(key: string, row: CompanyRow): SortValue | null {
  if (key === 'id') return { kind: 'code', value: row.id };
  if (key === 'name') return { kind: 'text', value: row.name };
  return null;
}
